// src/hooks/useChatNotifications.js
// ━━━ 聊天未读提醒 Hook（复用已有 socket 连接）━━━
// 借用 useWebSocket 的 socketRef，不创建独立连接。
// 只负责：监听 chat-message → 计数未读 + 播放提示音。
// 当 ChatWindow 打开时，ChatWindow 自己的 socket 也会加入聊天房间，
// 这里通过 useWebSocket 的 socket 也加入，两者不冲突（同房间多次 join 无副作用）。

import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 播放一声轻柔的"叮"提示音（Web Audio API，无需音频文件）
 */
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    // 880Hz → 660Hz，柔和下滑音
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
    setTimeout(() => ctx.close(), 500);
  } catch (e) {
    // 浏览器不支持或 autoplay 策略阻止，静默忽略
  }
}

function convKey(targetId) {
  return targetId === null || targetId === undefined ? 'public' : String(targetId);
}

function otherUserId(msg, myId) {
  return msg.sender_id === myId ? msg.recipient_id : msg.sender_id;
}

/**
 * @param {object} params
 * @param {number|null}           params.crewId       - 当前项目组 ID
 * @param {object|null}           params.user         - 当前用户对象
 * @param {boolean}               params.isChatOpen   - 聊天窗口是否已打开
 * @param {number|null}           params.chatTargetId - 当前激活的对话对象
 * @param {React.MutableRefObject} params.socketRef   - useWebSocket 返回的 socketRef（复用）
 *
 * @returns {{ unreadCounts: object, markRead: function, totalUnread: number }}
 */
export default function useChatNotifications({ crewId, user, isChatOpen, chatTargetId, socketRef }) {
  const [unreadCounts, setUnreadCounts] = useState({});

  const isChatOpenRef = useRef(isChatOpen);
  const chatTargetIdRef = useRef(chatTargetId);
  useEffect(() => { isChatOpenRef.current = isChatOpen; }, [isChatOpen]);
  useEffect(() => { chatTargetIdRef.current = chatTargetId; }, [chatTargetId]);

  useEffect(() => {
    if (!crewId || !user?.id || !socketRef) return;

    const handler = (msg) => {
      if (msg.sender_id === user.id) return;

      const key = msg.recipient_id
        ? convKey(otherUserId(msg, user.id))
        : 'public';

      const isViewing =
        isChatOpenRef.current &&
        convKey(chatTargetIdRef.current) === key;

      if (!isViewing) {
        playNotificationSound();
        setUnreadCounts(prev => ({
          ...prev,
          [key]: (prev[key] || 0) + 1,
        }));
      }
    };

    // 等 socket 连接就绪后加入聊天房间并监听
    const tryJoin = () => {
      const socket = socketRef.current;
      if (!socket) return;

      // 加入聊天房间（join 是幂等的，重复 join 无副作用）
      if (socket.connected) {
        socket.emit('join-crew-chat', crewId);
      }
      socket.on('connect', () => {
        socket.emit('join-crew-chat', crewId);
      });
      socket.on('chat-message', handler);
    };

    // socketRef.current 可能还没创建（useWebSocket 是异步初始化），轮询等待
    if (socketRef.current) {
      tryJoin();
    } else {
      const checkInterval = setInterval(() => {
        if (socketRef.current) {
          clearInterval(checkInterval);
          tryJoin();
        }
      }, 200);
      // 5秒后放弃
      const timeout = setTimeout(() => clearInterval(checkInterval), 5000);
      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }

    return () => {
      const socket = socketRef.current;
      if (socket) {
        socket.off('chat-message', handler);
        // 注意：不 disconnect socket，它属于 useWebSocket 管理
        // 也不 leave-crew-chat，因为可能 ChatWindow 还在用
      }
    };
  }, [crewId, user?.id, socketRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = useCallback((targetId) => {
    if (targetId === 'all') {
      setUnreadCounts({});
      return;
    }
    const key = convKey(targetId);
    setUnreadCounts(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const totalUnread = Object.values(unreadCounts).reduce((s, n) => s + n, 0);

  return { unreadCounts, markRead, totalUnread };
}
